import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { RouteComponentProps, useLocation } from 'react-router';
import { analytics } from '@chronos/firebase';

import MetaTags from 'react-meta-tags';
import LoginForm from './LoginForm';
import ForgotPassword from './ForgotPassword';
import WelcomeSlides from './WelcomeSlides';

import media from '../../shared/media';
import { useFirebaseUser } from '../../state';
import { hideScrollBars } from '../../style';
import { affiliateLinkPaths, getAffiliateSignupCompanion, getAffiliateSignupTopCompanion } from './AffiliateLoginCompanion';

export default function Login(routeProps: RouteComponentProps) {
  const { firebaseUser } = useFirebaseUser();

  const { pathname } = useLocation();
  const isLogin = pathname !== '/forgot-password';
  const isSignup = pathname === '/signup';
  const isAffiliatePath = affiliateLinkPaths.includes(pathname);

  if (isSignup) {
    analytics.logEvent('sign up - viewed : sign up page', {
      referring_url: window.location.href,
    });
  } else {
    analytics.logEvent('login - viewed : login page');
  }

  useEffect(() => {
    if (isSignup) {
      document.title = 'Sign Up for your free Chronos Time Tracking account';
    } else {
      document.title = 'Login | Chronos Time Tracking';
    }
  }, [isLogin, isSignup]);

  useEffect(() => {
    if (firebaseUser !== null) {
      routeProps.history.replace(`/${window.location.search}`);
    }
  }, [firebaseUser]); // eslint-disable-line

  const getTopSideComponent = () => {
    if (isAffiliatePath) {
      const componentToUse = getAffiliateSignupTopCompanion(pathname);

      if (componentToUse) {
        return componentToUse;
      }
    }

    return null;
  };

  const getRightSideComponent = () => {
    if (isAffiliatePath) {
      const componentToUse = getAffiliateSignupCompanion(pathname);

      if (componentToUse) {
        return componentToUse;
      }
    }

    return isSignup ? <WelcomeSlides /> : <BeachImage />;
  };

  if (isAffiliatePath) {
    return (
      <Container>
        <FlexScroll>
          { getTopSideComponent() }
          <LoginForm {...routeProps} />
        </FlexScroll>
        { getRightSideComponent() }
      </Container>
    );
  }

  return (
    <Container>
      <FlexScroll>
        {
          isLogin ? (
            <LoginForm {...routeProps} />
          ) : (
            <ForgotPassword />
          )
        }
      </FlexScroll>

      { isSignup ? <WelcomeSlides /> : <BeachImage />}

      { isSignup && (
        <>
          <link rel="canonical" href="https://app.chronostimetracking.com/signup" />
          <MetaTags>
            <meta name="description" content="Sign up for Chronos Time Tracking, smart time tracking for freelancers.  It's free!" />
          </MetaTags>
        </>
      )}
      { !isSignup && (
        <>
          <link rel="canonical" href="https://app.chronostimetracking.com/login" />
          <MetaTags>
            <meta name="description" content="Login to Chronos Time Tracking, smart time tracking for freelancers.  Track time, get insights, and make more money." />
          </MetaTags>
        </>
      )}
    </Container>
  );
}

const Container = styled.div({
  height: '100vh',
  width: '100vw',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
});

const FlexScroll = styled.div({
  flex: 1,
  maxHeight: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
  ...hideScrollBars,
});

const BeachImage = styled.div({
  background: 'url(\'images/islands.png\') no-repeat center / cover',
  height: '100%',
  width: '53%',
  [media('small')]: {
    display: 'none',
  },
});
